import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirmName } from '../contexts/OrgSettingsContext';
import { PLATFORM_SHORT_NAME } from '../branding';
import GlobalSearch from './GlobalSearch';
import MobileLayout from './mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { isUserActive } from '../utils/activity';
import { getNotifications, markNotificationRead, markAllNotificationsRead, getAnnouncements, createAnnouncement, updateAnnouncement, getChatUnreadCount } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AppNotification {
  id: number;
  title: string;
  message: string;
  isRead: boolean;
  link?: string;
  createdAt: string;
}

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}


// ─── NotificationBell component ───────────────────────────────────────────────

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications();
      const data = res.data;
      if (Array.isArray(data)) {
        setNotifications(data.slice(0, 10));
        setUnreadCount(data.filter((n: AppNotification) => !n.isRead).length);
      } else {
        setNotifications((data.notifications ?? []).slice(0, 10));
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch {
      // silently ignore polling errors
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(() => {
      if (isUserActive()) fetchNotifications();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleNotificationClick = async (n: AppNotification) => {
    if (!n.isRead) {
      try {
        await markNotificationRead(n.id);
        setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch { /* ignore */ }
    }
    if (n.link) { setOpen(false); navigate(n.link); }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors focus:outline-none"
        aria-label="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-medium">
                  {unreadCount} new
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors ml-1" aria-label="Close notifications">✕</button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-80">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-sm gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                No notifications
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left flex gap-0 border-b border-gray-50 last:border-b-0 hover:bg-gray-50 transition-colors focus:outline-none ${n.link ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <div className={`w-1 shrink-0 rounded-l ${n.isRead ? 'bg-transparent' : 'bg-blue-500'}`} />
                  <div className="px-3 py-3 flex-1 min-w-0">
                    <p className={`text-sm leading-snug truncate ${n.isRead ? 'font-normal text-gray-700' : 'font-semibold text-gray-900'}`}>{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-snug">{n.message}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{relativeTime(n.createdAt)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── AnnouncementBell component ──────────────────────────────────────────────

interface Announcement {
  id: number; title: string; content: string;
  isPinned: boolean; expiresAt?: string | null;
  createdBy: { id: number; staffName: string };
  createdAt: string; updatedAt: string;
}

const AnnouncementBell: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isHR, isPartner, user } = useAuth();
  const canPost = isAdmin || isHR || isPartner;
  const canPin  = isAdmin || isHR;
  const [open, setOpen]               = useState(false);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unread, setUnread]           = useState(0);
  const [showPost, setShowPost]       = useState(false);
  const [postTitle, setPostTitle]     = useState('');
  const [postContent, setPostContent] = useState('');
  const [postPinned, setPostPinned]   = useState(false);
  const [postExpiry, setPostExpiry]   = useState('');
  const [posting, setPosting]         = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const SEEN_KEY = 'announcements_seen_at';

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await getAnnouncements();
      const now = new Date();
      const active = (res.data as Announcement[]).filter(
        (a) => !a.expiresAt || new Date(a.expiresAt) >= now
      );
      setAnnouncements(active);
      const seenAt = localStorage.getItem(SEEN_KEY);
      const seenDate = seenAt ? new Date(seenAt) : new Date(0);
      setUnread(active.filter((a) => new Date(a.createdAt) > seenDate).length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
    const id = setInterval(() => {
      if (isUserActive()) fetchAnnouncements();
    }, 60000);
    return () => clearInterval(id);
  }, [fetchAnnouncements]);

  useEffect(() => {
    if (!open) return;
    // Mark all as seen when dropdown opens
    localStorage.setItem(SEEN_KEY, new Date().toISOString());
    setUnread(0);
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!postTitle.trim() || !postContent.trim()) return;
    setPosting(true);
    try {
      await createAnnouncement({ title: postTitle.trim(), content: postContent.trim(), isPinned: postPinned, expiresAt: postExpiry || null });
      setShowPost(false); setPostTitle(''); setPostContent(''); setPostPinned(false); setPostExpiry('');
      fetchAnnouncements();
    } finally { setPosting(false); }
  };

  const handlePin = async (a: Announcement) => {
    await updateAnnouncement(a.id, { isPinned: !a.isPinned });
    fetchAnnouncements();
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors focus:outline-none"
        aria-label="Announcements"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold leading-none px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">
              📢 Announcements
              {unread > 0 && (
                <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{unread} new</span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {canPost && (
                <button onClick={() => setShowPost((s) => !s)} className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors">
                  + Post
                </button>
              )}
              <button onClick={() => { setOpen(false); navigate('/staff-management'); }} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
                Manage
              </button>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors ml-1" aria-label="Close">✕</button>
            </div>
          </div>

          {showPost && (
            <form onSubmit={handlePost} className="px-4 py-3 border-b border-gray-100 bg-gray-50 space-y-2">
              <input
                className="input-field text-sm" placeholder="Title *" value={postTitle}
                onChange={(e) => setPostTitle(e.target.value)} autoFocus required
              />
              <textarea
                className="input-field text-sm resize-none" rows={3} placeholder="Content *"
                value={postContent} onChange={(e) => setPostContent(e.target.value)} required
              />
              <div className="flex items-center gap-3 flex-wrap">
                <input type="date" className="input-field text-sm flex-1 min-w-0" value={postExpiry}
                  onChange={(e) => setPostExpiry(e.target.value)} title="Expires on (optional)" />
                {canPin && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" className="rounded" checked={postPinned} onChange={(e) => setPostPinned(e.target.checked)} />
                    📌 Pin
                  </label>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-secondary text-xs py-1" onClick={() => setShowPost(false)}>Cancel</button>
                <button type="submit" className="btn-primary text-xs py-1" disabled={posting}>{posting ? 'Posting…' : 'Post'}</button>
              </div>
            </form>
          )}

          <div className="overflow-y-auto max-h-80">
            {announcements.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 text-sm gap-1">
                <span className="text-2xl">📢</span>
                No announcements
              </div>
            ) : (
              announcements.map((a) => (
                <div key={a.id} className={`px-4 py-3 border-b border-gray-50 last:border-b-0 ${a.isPinned ? 'bg-amber-50/40' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        {a.isPinned && <span className="text-xs text-amber-600 font-semibold">📌</span>}
                        <span className="text-sm font-semibold text-gray-900 truncate">{a.title}</span>
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{a.content}</p>
                      <p className="text-[11px] text-gray-400 mt-1">
                        {a.createdBy.staffName} · {relativeTime(a.createdAt)}
                      </p>
                    </div>
                    {canPin && (
                      <button
                        onClick={() => handlePin(a)}
                        title={a.isPinned ? 'Unpin' : 'Pin'}
                        className="text-gray-300 hover:text-amber-500 transition-colors text-sm shrink-0 mt-0.5"
                      >
                        {a.isPinned ? '📌' : '📎'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── SidebarNavGroup component ────────────────────────────────────────────────

interface SidebarNavGroupProps {
  items: NavItem[];
  first?: boolean;
}

const SidebarNavGroup: React.FC<SidebarNavGroupProps & { chatUnread?: number }> = ({ items, first, chatUnread }) => (
  <>
    {!first && <div className="mx-3 my-1.5 border-t border-gray-300" />}
    {items.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) =>
          `flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
          }`
        }
      >
        <span className="text-base shrink-0">{item.icon}</span>
        <span className="truncate flex-1">{item.label}</span>
        {item.to === '/chat' && (chatUnread ?? 0) > 0 && (
          <span className="min-w-[18px] h-[18px] rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
            {(chatUnread ?? 0) > 99 ? '99+' : chatUnread}
          </span>
        )}
      </NavLink>
    ))}
  </>
);

// ─── Layout ───────────────────────────────────────────────────────────────────

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout, isAdmin, isHR, isPartner, canSwitchView, viewMode, setViewMode } = useAuth();
  const firmName = useFirmName();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  useEffect(() => {
    const fetchChatUnread = async () => {
      try {
        const res = await getChatUnreadCount();
        setChatUnread(res.data.count ?? 0);
      } catch { /* ignore */ }
    };
    fetchChatUnread();
    const id = setInterval(() => {
      if (isUserActive()) fetchChatUnread();
    }, 15000);
    return () => clearInterval(id);
  }, []);

  // Ctrl+K search shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Each array is a visual group separated by a divider
  const navSections: NavItem[][] = [
    [
      { to: '/dashboard', label: 'Dashboard', icon: '📊' },
      { to: '/reports',   label: 'Reports',   icon: '📈' },
      { to: '/chat',      label: 'Team Chat',  icon: '💬' },
    ],
    [
      { to: '/tasks',      label: 'Tasks',     icon: '📋' },
      { to: '/timesheets', label: 'Timesheet', icon: '⏱️' },
    ],
    [
      { to: '/clients',        label: 'Clients',        icon: '🏢' },
      { to: '/invoices', label: 'Invoices', icon: '🧾' },
      // Reconciliation is now a sub-tab inside Invoices, not a top-level page.
      { to: '/reimbursements', label: 'Reimbursements', icon: '💸' },
    ],
    [
      { to: '/leaves',     label: 'Leave & Calendar', icon: '🏖️' },
      { to: '/attendance', label: 'Attendance',        icon: '📅' },
      { to: '/tickets',    label: 'Tickets',           icon: '🎫' },
      { to: '/rules',      label: 'My Rules',          icon: '⚡' },
      ...(!(isAdmin || isHR || isPartner) ? [{ to: '/payroll', label: 'My Payslips', icon: '🧾' }] : []),
    ],
    ...(isAdmin || isPartner ? [[
      { to: '/leads',   label: 'Leads',   icon: '🎯' },
      { to: '/tenders', label: 'Tenders', icon: '📋' },
    ]] : []),
    ...(isAdmin || isHR || isPartner ? [[
      { to: '/payroll', label: 'Payroll', icon: '💵' },
      ...(isAdmin || isHR ? [{ to: '/payments', label: 'Payments', icon: '🏦' }] : []),
    ]] : []),
    // The administrative half of the sidebar. `isAdmin` follows the current
    // view, so partner view drops both groups — the point of the switch below.
    ...(isAdmin || isHR ? [[
      { to: '/staff-management', label: 'Staff Management', icon: '👤' },
      { to: '/masters',          label: 'Masters',          icon: '🗂️' },
      { to: '/audit',            label: 'Audit Log',        icon: '🔍' },
    ]] : []),
    ...(isAdmin ? [[
      { to: '/org-settings',  label: 'Org Settings',   icon: '⚙️' },
      { to: '/deleted-items', label: 'Deleted Items',  icon: '🗑️' },
    ]] : []),
  ];

  const handleLogout = () => { logout(); navigate('/login'); };

  // Below the md breakpoint the desktop shell can't work — a 208px sidebar
  // leaves ~119px of content on a phone — so hand over to the mobile shell
  // entirely rather than trying to make one layout serve both.
  if (isMobile) {
    return <MobileLayout bell={<NotificationBell />}>{children}</MobileLayout>;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}

      {/* Sidebar — fixed width, no collapse */}
      <aside className="w-52 bg-white border-r border-gray-200 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-4 py-4 border-b border-gray-100">
          {/* The firm the signed-in user works for — not the product name. A user
              at another firm seeing "MGSG" here would reasonably wonder whose
              system they were in. */}
          <span className="font-bold text-blue-700 text-lg tracking-tight truncate block" title={firmName}>
            {firmName || PLATFORM_SHORT_NAME}
          </span>
        </div>

        {/* Search trigger */}
        <div className="px-2 pt-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-gray-500 bg-gray-50 hover:bg-gray-100 transition-colors border border-gray-200"
          >
            <span>🔍</span>
            <span className="flex-1 text-left text-xs">Search… <kbd className="ml-1 text-gray-400">Ctrl+K</kbd></span>
          </button>
        </div>

        {/* Nav sections separated by dividers */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {navSections.map((items, i) => (
            <SidebarNavGroup key={i} items={items} first={i === 0} chatUnread={i === 0 ? chatUnread : 0} />
          ))}
        </nav>

        {/* Bottom — view switch + profile + logout */}
        <div className="px-2 py-3 border-t border-gray-100 space-y-0.5">
          {canSwitchView && (
            <div className="px-1 pb-2.5">
              <div className="flex rounded-lg bg-gray-100 p-0.5">
                {(['partner', 'admin'] as const).map((mode) => {
                  const active = mode === viewMode;
                  return (
                    <button
                      key={mode}
                      onClick={() => { if (!active) setViewMode(mode); }}
                      aria-pressed={active}
                      title={mode === 'partner'
                        ? 'Partner view — hides the admin pages, deletions and opening-balance corrections'
                        : 'Admin view — full access'}
                      className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                        active ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {mode === 'partner' ? 'Partner' : 'Admin'}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`
            }
          >
            <span className="text-base">👤</span>
            <span>Profile</span>
          </NavLink>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
          >
            <span className="text-base">🚪</span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <header className="bg-white border-b border-gray-200 px-6 py-3.5 flex items-center justify-between shrink-0">
          <h1 className="text-base font-semibold text-gray-800">Office Management System</h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden sm:flex items-center gap-2 text-sm text-gray-500 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <span>🔍</span>
              <span>Search</span>
              <kbd className="text-xs bg-white text-gray-400 px-1.5 py-0.5 rounded border">Ctrl+K</kbd>
            </button>
            <AnnouncementBell />
            <NotificationBell />
            <span className="text-sm text-gray-500">{user?.staffName || user?.email}</span>
            {isAdmin
              ? <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Admin</span>
              : canSwitchView && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">Partner</span>}
          </div>
        </header>
        <main className="flex-1 overflow-auto p-6">{children}</main>
      </div>
    </div>
  );
};
